/**
 * HTTP-клиент API.
 *
 * Две вещи, которые он делает и о которых легко забыть в каждом вызове:
 *  • подставляет CSRF-заголовок на мутирующие запросы;
 *  • нормализует ошибки в один тип, чтобы UI не разбирал сырые Response.
 *
 * Чего он НЕ делает: не принимает решений о правах. Скрытая кнопка — это UX;
 * запрет живёт на сервере. Клиент обязан быть готов получить 403 на любой
 * запрос, даже если по его представлению право есть.
 */

import { noteResponse } from './pwa.ts';
import type {
  AnyTelegramAccountDto,
  AuditEventDto,
  CurrentUserDto,
  TelegramChatDto,
  TelegramMessageDto,
  TelegramPresenceDto,
  TelegramTextEntityDto,
  UserDto,
  UserSummaryDto,
  StayAccountCredentialDto,
  StayAccountDto,
  StayBookingDto,
  StayBookingFacetsDto,
  StayBookingListDto,
  StayCaptureInput,
  StayCaptureResultDto,
  StayCommentDto,
  StayPersonDto,
  StayPlatform,
  StayPropertyDetailsDto,
  StayPropertyDto,
  StayPropertyLookupDto,
  StayPropertyStatus,
} from '@tw/shared-types';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Нет прав. UI показывает это как «недоступно», а не как сбой. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** Сессия истекла — нужно на экран входа. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /**
   * Состояние клиента разошлось с сервером (§28 ТЗ: выбран не тот аккаунт).
   * Требует не повтора, а обновления данных и подтверждения пользователем.
   */
  get isConflict(): boolean {
    return this.status === 409;
  }
}

const CSRF_COOKIE = 'tw_csrf';

function readCsrfToken(): string | null {
  // Cookie специально не HttpOnly — это половина double-submit защиты.
  const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`));
  return match ? decodeURIComponent(match[1]!) : null;
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * Заголовок double-submit для небезопасных методов.
 *
 * Вынесен из request() не ради красоты. sendMedia шлёт сырые байты, поэтому
 * через request() не проходит, и вместе с общим кодом потерял добавление
 * x-csrf-token: отправка ЛЮБОГО файла возвращала 403, не дойдя до Telegram.
 * Ошибка не была молчаливой, но выглядела как отказ в правах — то есть как
 * проблема доступа, а не как забытый заголовок, и объяснялась неверно.
 *
 * Пока функция одна на всех, следующий запрос мимо request() получит
 * заголовок по построению, а не по памяти автора.
 */
function csrfHeaders(method: string): Record<string, string> {
  if (SAFE_METHODS.has(method)) return {};
  const csrf = readCsrfToken();
  return csrf ? { 'x-csrf-token': csrf } : {};
}

export interface ApiClientOptions {
  baseUrl?: string;
  /** Вызывается при 401 — обычно сбрасывает сессию и уводит на вход. */
  onUnauthorized?: () => void;
}

/** Разделы вложений чата. Совпадают с вкладками в карточке чата. */
export type MediaSection = 'media' | 'files' | 'voice' | 'links';

export class ApiClient {
  private readonly baseUrl: string;
  private readonly onUnauthorized: (() => void) | undefined;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = (options.baseUrl ?? import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');
    this.onUnauthorized = options.onUnauthorized;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    signal?: AbortSignal,
  ): Promise<T> {
    const headers: Record<string, string> = {};

    if (body !== undefined) headers['content-type'] = 'application/json';

    Object.assign(headers, csrfHeaders(method));

    const response = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      // Сессия живёт в HttpOnly-cookie: без credentials она не уйдёт.
      credentials: 'include',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });

    // Ответ из сохранённого на телефоне (офлайн, PWA) — плашке «нет сети».
    if (method === 'GET') noteResponse(response);

    if (response.status === 204) return undefined as T;

    const payload = (await response.json().catch(() => null)) as
      | { error?: { code?: string; message?: string; details?: unknown } }
      | null;

    if (!response.ok) {
      if (response.status === 401) this.onUnauthorized?.();

      throw new ApiError(
        response.status,
        payload?.error?.code ?? 'UNKNOWN',
        payload?.error?.message ?? `Запрос завершился с кодом ${response.status}`,
        payload?.error?.details,
      );
    }

    return payload as T;
  }

  // ── Аутентификация ─────────────────────────────────────────────────────────

  login(email: string, password: string) {
    return this.request<{ user: CurrentUserDto; csrfToken: string }>(
      'POST',
      '/api/auth/login',
      { email, password, deviceLabel: describeDevice() },
    );
  }

  logout() {
    return this.request<{ ok: true }>('POST', '/api/auth/logout');
  }

  me() {
    return this.request<CurrentUserDto>('GET', '/api/auth/me');
  }

  changePassword(currentPassword: string, newPassword: string) {
    return this.request<{ ok: true }>('POST', '/api/auth/password', {
      currentPassword,
      newPassword,
    });
  }

  // ── Telegram-аккаунты ──────────────────────────────────────────────────────

  listAccounts() {
    return this.request<{ items: AnyTelegramAccountDto[] }>('GET', '/api/telegram/accounts');
  }

  connectAccount(input: { method: 'phone' | 'qr'; phone?: string; alias?: string }) {
    return this.request<{ account: AnyTelegramAccountDto; auth: AuthStateDto }>(
      'POST',
      '/api/telegram/accounts',
      input,
    );
  }

  submitAuthCode(accountId: string, code: string) {
    return this.request<{ auth: AuthStateDto }>(
      'POST',
      `/api/telegram/accounts/${accountId}/auth/code`,
      { code },
    );
  }

  /** Премиальные эмодзи аккаунта — наборы для пикера. */
  customEmojiSets(accountId: string) {
    return this.request<{ sets: CustomEmojiSetDto[] }>(
      'GET',
      `/api/telegram/accounts/${accountId}/custom-emoji-list`,
    );
  }

  /** Папки Telegram аккаунта — вкладки как в самом Telegram. */
  telegramFolders(accountId: string) {
    return this.request<{ items: TgFolderDto[] }>(
      'GET',
      `/api/telegram/accounts/${accountId}/tg-folders`,
    );
  }

  /** Текущее состояние авторизации аккаунта — когда ответ на шаг пришёл раньше апдейта. */
  accountAuthState(accountId: string) {
    return this.request<{ accountId: string; connectionState: string; auth: AuthStateDto }>(
      'GET',
      `/api/telegram/accounts/${accountId}/state`,
    );
  }

  /** Перейти на QR посреди входа по номеру: без кода вообще. */
  switchAuthToQr(accountId: string) {
    return this.request<{ auth: AuthStateDto }>(
      'POST',
      `/api/telegram/accounts/${accountId}/auth/qr`,
      {},
    );
  }

  /** Попросить код ещё раз — Telegram пришлёт его другим способом. */
  resendAuthCode(accountId: string) {
    return this.request<{ auth: AuthStateDto }>(
      'POST',
      `/api/telegram/accounts/${accountId}/auth/resend`,
      {},
    );
  }

  submitAuthPassword(accountId: string, password: string) {
    return this.request<{ auth: AuthStateDto }>(
      'POST',
      `/api/telegram/accounts/${accountId}/auth/password`,
      { password },
    );
  }

  updateAccount(accountId: string, patch: { alias?: string }) {
    return this.request<AnyTelegramAccountDto>(
      'PATCH',
      `/api/telegram/accounts/${accountId}`,
      patch,
    );
  }

  /**
   * Удаление аккаунта из системы. Необратимо и отличается от отключения:
   * отключение рвёт сессию, удаление стирает и назначения, и привязку чатов.
   */
  deleteAccount(accountId: string) {
    return this.request<void>('DELETE', `/api/telegram/accounts/${accountId}`);
  }

  disconnectAccount(accountId: string) {
    return this.request<{ ok: true }>(
      'POST',
      `/api/telegram/accounts/${accountId}/disconnect`,
    );
  }

  // ── Быстрые ответы ─────────────────────────────────────────────────────────

  listQuickReplies() {
    return this.request<{ items: QuickReplyDto[] }>('GET', '/api/quick-replies');
  }

  createQuickReply(input: {
    title: string;
    text: string;
    sortOrder?: number;
    folderId?: string | null;
    entities?: TelegramTextEntityDto[];
  }) {
    return this.request<QuickReplyDto>('POST', '/api/quick-replies', input);
  }

  updateQuickReply(
    id: string,
    patch: { title?: string; text?: string; sortOrder?: number; folderId?: string | null; entities?: TelegramTextEntityDto[] },
  ) {
    return this.request<QuickReplyDto>('PATCH', `/api/quick-replies/${id}`, patch);
  }

  /** Заготовка из сообщения переписки — с разметкой и вложением. */
  createQuickReplyFromMessage(input: {
    chatId: string;
    telegramMessageId: string;
    title: string;
    folderId?: string | null;
  }) {
    return this.request<QuickReplyDto>('POST', '/api/quick-replies/from-message', input);
  }

  /** Прикрепить фото или видео к заготовке. Тело — сам файл. */
  async uploadQuickReplyAttachment(id: string, file: File): Promise<QuickReplyAttachmentDto> {
    const response = await fetch(`/api/quick-replies/${id}/attachments`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'content-type': file.type || 'application/octet-stream',
        'x-file-name': encodeURIComponent(file.name),
        ...csrfHeaders('POST'),
      },
      body: file,
    });
    if (!response.ok) {
      const payload = (await response.json().catch(() => null)) as
        | { error?: { code?: string; message?: string } }
        | null;
      if (response.status === 401) this.onUnauthorized?.();
      throw new ApiError(
        response.status,
        payload?.error?.code ?? 'UNKNOWN',
        payload?.error?.message ?? 'Не удалось загрузить файл',
      );
    }
    return (await response.json()) as QuickReplyAttachmentDto;
  }

  deleteQuickReplyAttachment(id: string, attachmentId: string) {
    return this.request<void>('DELETE', `/api/quick-replies/${id}/attachments/${attachmentId}`);
  }

  quickReplyAttachmentUrl(id: string, attachmentId: string): string {
    return `/api/quick-replies/${id}/attachments/${attachmentId}`;
  }

  /** Отправить заготовку целиком: текст с разметкой и вложения. */
  sendQuickReply(
    chatId: string,
    input: {
      telegramAccountId: string;
      quickReplyId: string;
      idempotencyKey: string;
      replyToTelegramMessageId?: string;
    },
  ) {
    return this.request<TelegramMessageDto>('POST', `/api/chats/${chatId}/quick-reply`, input);
  }

  deleteQuickReply(id: string) {
    return this.request<void>('DELETE', `/api/quick-replies/${id}`);
  }

  // ── Папки быстрых ответов ─────────────────────────────────────────────────

  quickReplyFolders() {
    return this.request<{ items: QuickReplyFolderDto[] }>('GET', '/api/quick-reply-folders');
  }

  createQuickReplyFolder(body: { name: string; sortOrder?: number }) {
    return this.request<QuickReplyFolderDto>('POST', '/api/quick-reply-folders', body);
  }

  updateQuickReplyFolder(id: string, body: { name?: string; sortOrder?: number }) {
    return this.request<QuickReplyFolderDto>('PATCH', `/api/quick-reply-folders/${id}`, body);
  }

  deleteQuickReplyFolder(id: string) {
    return this.request<void>('DELETE', `/api/quick-reply-folders/${id}`);
  }

  grantQuickReplyFolder(id: string, subject: { userId?: string; role?: string; teamId?: string }) {
    return this.request<{ ok: true }>('POST', `/api/quick-reply-folders/${id}/access`, subject);
  }

  revokeQuickReplyFolder(id: string, accessId: string) {
    return this.request<void>('DELETE', `/api/quick-reply-folders/${id}/access/${accessId}`);
  }

  // ── Inbox и чаты ───────────────────────────────────────────────────────────

  inbox(params: InboxParams = {}) {
    return this.request<{ items: TelegramChatDto[]; nextCursor: string | null }>(
      'GET',
      `/api/inbox${buildQuery(params)}`,
    );
  }

  // ── Закрепление, реакции, стикеры, беззвучность ──────────────────────────

  /** Вложения чата разделами. `from` — курсор, идентификатор последнего показанного. */
  chatMediaList(chatId: string, params: { section: MediaSection; limit?: number; from?: string }) {
    return this.request<{ items: TelegramMessageDto[] }>(
      'GET',
      `/api/chats/${chatId}/media-list${buildQuery(params)}`,
    );
  }

  chatPresence(chatId: string) {
    return this.request<{ presence: TelegramPresenceDto }>('GET', `/api/chats/${chatId}/presence`);
  }

  pinnedMessage(chatId: string) {
    return this.request<{ message: TelegramMessageDto | null }>('GET', `/api/chats/${chatId}/pinned`);
  }

  /** Все закреплённые сообщения чата. */
  pinnedMessages(chatId: string) {
    return this.request<{ items: TelegramMessageDto[] }>('GET', `/api/chats/${chatId}/pinned-all`);
  }

  pinMessage(chatId: string, messageId: string, onlyForSelf = false) {
    return this.request<void>('POST', `/api/chats/${chatId}/messages/${messageId}/pin`, {
      onlyForSelf,
    });
  }

  unpinMessage(chatId: string, messageId: string) {
    return this.request<void>('DELETE', `/api/chats/${chatId}/messages/${messageId}/pin`);
  }

  addReaction(chatId: string, messageId: string, emoji: string) {
    return this.request<void>('POST', `/api/chats/${chatId}/messages/${messageId}/reaction`, {
      emoji,
    });
  }

  removeReaction(chatId: string, messageId: string, emoji: string) {
    return this.request<void>(
      'DELETE',
      `/api/chats/${chatId}/messages/${messageId}/reaction?emoji=${encodeURIComponent(emoji)}`,
    );
  }

  stickerSets(chatId: string) {
    return this.request<{ sets: StickerSetDto[] }>('GET', `/api/chats/${chatId}/stickers`);
  }

  sendSticker(
    chatId: string,
    body: { telegramAccountId: string; fileId: string; replyToTelegramMessageId?: string },
  ) {
    return this.request<TelegramMessageDto>('POST', `/api/chats/${chatId}/sticker`, body);
  }

  /** Беззвучность чата — поимённая и только для наших оповещений. */
  muteChat(chatId: string) {
    return this.request<void>('POST', `/api/chats/${chatId}/mute`);
  }

  unmuteChat(chatId: string) {
    return this.request<void>('DELETE', `/api/chats/${chatId}/mute`);
  }

  /** Поиск по переписке одного чата. */
  searchInChat(chatId: string, params: { q: string; limit?: number }) {
    return this.request<{ items: TelegramMessageDto[] }>(
      'GET',
      `/api/chats/${chatId}/search${buildQuery(params)}`,
    );
  }

  /**
   * Поиск по Telegram.
   *
   * Отдельно от inbox и вызывается ТОЛЬКО явным действием пользователя.
   * Автоподсказка по каждой букве здесь недопустима: запрос уходит на серверы
   * Telegram, лимиты считаются на аккаунт, и набор слова из шести букв стоил бы
   * шести обращений — прямая дорога к flood-wait для всей компании.
   */
  telegramSearch(params: { telegramAccountId: string; q: string; limit?: number }) {
    return this.request<{ items: TelegramSearchItemDto[] }>(
      'GET',
      `/api/telegram/search${buildQuery(params)}`,
    );
  }

  /** Открыть переписку по @username или по идентификатору пользователя. */
  openTelegramChat(body: {
    telegramAccountId: string;
    username?: string;
    telegramUserId?: string;
  }) {
    return this.request<TelegramChatDto>('POST', '/api/telegram/chats/open', body);
  }

  /** Добавить контакт по номеру. Меняет записную книжку рабочего аккаунта. */
  addTelegramContact(body: {
    telegramAccountId: string;
    phone: string;
    firstName: string;
    lastName?: string;
  }) {
    return this.request<TelegramChatDto>('POST', '/api/telegram/contacts', body);
  }

  // ── Разделы рабочего места ───────────────────────────────────────────────

  /** Разделы, доступные текущему сотруднику. */
  sections() {
    return this.request<{ items: SectionDto[] }>('GET', '/api/sections');
  }

  /** Все разделы вместе с выдачами. Только для управления. */
  manageSections() {
    return this.request<{ items: ManagedSectionDto[] }>('GET', '/api/sections/manage');
  }

  createSection(body: {
    /** Необязателен: без него сервер соберёт ключ из названия. */
    key?: string;
    title: string;
    kind: 'EMBEDDED' | 'BROWSER';
    url?: string;
    allowedHosts?: string[];
    sortOrder?: number;
    loginSelector?: string;
    passwordSelector?: string;
    submitSelector?: string;
  }) {
    return this.request<SectionDto>('POST', '/api/sections', body);
  }

  updateSection(
    id: string,
    body: {
      title?: string;
      url?: string | null;
      allowedHosts?: string[];
      enabled?: boolean;
      sortOrder?: number;
      loginSelector?: string | null;
      passwordSelector?: string | null;
      submitSelector?: string | null;
    },
  ) {
    return this.request<SectionDto>('PATCH', `/api/sections/${id}`, body);
  }

  // ── Оповещения рабочих систем ──────────────────────────────────────────────

  /**
   * Мои настройки оповещений.
   *
   * Возвращаются ТОЛЬКО изменённые разделы. Отсутствие записи означает
   * состояние по умолчанию — оповещения включены.
   */
  sectionNotifications() {
    return this.request<{ items: Array<{ sectionId: string; enabled: boolean }> }>(
      'GET',
      '/api/me/section-notifications',
    );
  }

  setSectionNotifications(sectionId: string, enabled: boolean) {
    return this.request<{ ok: true }>(
      'PUT',
      `/api/me/section-notifications/${sectionId}`,
      { enabled },
    );
  }

  // ── Мои входы в рабочие системы ────────────────────────────────────────────

  /** Мои пары. Пароли не возвращаются — только признак заполненности. */
  sectionCredentials() {
    return this.request<{ items: SectionCredentialDto[] }>(
      'GET',
      '/api/me/section-credentials',
    );
  }

  saveSectionCredential(sectionId: string, body: { login: string; password: string }) {
    return this.request<{ ok: true }>(
      'PUT',
      `/api/me/section-credentials/${sectionId}`,
      body,
    );
  }

  deleteSectionCredential(sectionId: string) {
    return this.request<{ ok: true }>(
      'DELETE',
      `/api/me/section-credentials/${sectionId}`,
    );
  }

  /**
   * Расшифрованная пара — для подстановки в форму входа.
   *
   * Ответ живёт в памяти и уходит в оболочку. Ни в состояние React, ни в
   * localStorage он попадать не должен: там его переживёт перезагрузка.
   */
  revealSectionCredential(sectionId: string) {
    return this.request<{ login: string; password: string }>(
      'POST',
      `/api/me/section-credentials/${sectionId}/reveal`,
    );
  }

  // ── Апартаменты (docs/STAYS.md) ──

  stayAccounts() {
    return this.request<{ items: StayAccountDto[] }>('GET', '/api/stays/accounts');
  }

  createStayAccount(body: {
    platform: StayPlatform;
    title: string;
    login: string;
    password?: string;
    note?: string | null;
  }) {
    return this.request<StayAccountDto>('POST', '/api/stays/accounts', body);
  }

  updateStayAccount(
    id: string,
    body: Partial<{ title: string; login: string; password: string; note: string | null; enabled: boolean }>,
  ) {
    return this.request<StayAccountDto>('PATCH', `/api/stays/accounts/${id}`, body);
  }

  deleteStayAccount(id: string) {
    return this.request<void>('DELETE', `/api/stays/accounts/${id}`);
  }

  grantStayAccount(id: string, userId: string) {
    return this.request<{ ok: true }>('POST', `/api/stays/accounts/${id}/access`, { userId });
  }

  revokeStayAccount(id: string, userId: string) {
    return this.request<void>('DELETE', `/api/stays/accounts/${id}/access/${userId}`);
  }

  /** Пара для автовхода — только для передачи оболочке, не для показа. */
  revealStayAccount(id: string) {
    return this.request<StayAccountCredentialDto>('POST', `/api/stays/accounts/${id}/reveal`);
  }

  /** Только логин аккаунта площадки — «Скопировать логин» на телефоне. */
  stayAccountLogin(id: string) {
    return this.request<{ login: string }>('GET', `/api/stays/accounts/${id}/login`);
  }

  stayPeople() {
    return this.request<{ items: StayPersonDto[] }>('GET', '/api/stays/people');
  }

  stayProperties(
    params: { q?: string; status?: StayPropertyStatus; platform?: StayPlatform; city?: string; country?: string } = {},
  ) {
    return this.request<{ items: StayPropertyDto[] }>(
      'GET',
      `/api/stays/properties${buildQuery(params)}`,
    );
  }

  /** Страны и города базы квартир — для выпадающих фильтров. */
  stayPropertyFacets() {
    return this.request<StayBookingFacetsDto>('GET', '/api/stays/properties/facets');
  }

  /** Карточка базы для страницы во встроенном браузере. */
  lookupStayProperty(url: string, title?: string) {
    return this.request<StayPropertyLookupDto>(
      'GET',
      `/api/stays/properties/lookup${buildQuery({ url, ...(title ? { title } : {}) })}`,
    );
  }

  stayProperty(id: string) {
    return this.request<StayPropertyDetailsDto>('GET', `/api/stays/properties/${id}`);
  }

  saveStayProperty(body: {
    url: string;
    title: string;
    city?: string | null;
    country?: string | null;
    address?: string | null;
    photoUrl?: string | null;
  }) {
    return this.request<StayPropertyDto>('POST', '/api/stays/properties', body);
  }

  updateStayProperty(
    id: string,
    body: Partial<{
      /** Привязать ссылку на объявление к карточке без неё. */
      url: string;
      title: string;
      city: string | null;
      country: string | null;
      address: string | null;
      photoUrl: string | null;
      status: StayPropertyStatus;
      statusNote: string | null;
    }>,
  ) {
    return this.request<StayPropertyDto>('PATCH', `/api/stays/properties/${id}`, body);
  }

  deleteStayProperty(id: string) {
    return this.request<void>('DELETE', `/api/stays/properties/${id}`);
  }

  addStayComment(propertyId: string, body: { rating?: number | null; text: string }) {
    return this.request<StayCommentDto>('POST', `/api/stays/properties/${propertyId}/comments`, body);
  }

  deleteStayComment(id: string) {
    return this.request<void>('DELETE', `/api/stays/comments/${id}`);
  }

  /** Страны и города броней — для выпадающих фильтров таблицы. */
  stayBookingFacets() {
    return this.request<StayBookingFacetsDto>('GET', '/api/stays/bookings/facets');
  }

  stayBookings(params: StayBookingQuery = {}) {
    return this.request<StayBookingListDto>('GET', `/api/stays/bookings${buildQuery(params)}`);
  }

  createStayBooking(body: StayBookingInput) {
    return this.request<StayBookingDto>('POST', '/api/stays/bookings', body);
  }

  updateStayBooking(id: string, body: Partial<StayBookingInput>) {
    return this.request<StayBookingDto>('PATCH', `/api/stays/bookings/${id}`, body);
  }

  deleteStayBooking(id: string) {
    return this.request<void>('DELETE', `/api/stays/bookings/${id}`);
  }

  // ── Пуш-уведомления (PWA) ──

  pushKey() {
    return this.request<{ publicKey: string | null }>('GET', '/api/push/key');
  }

  pushSubscribe(body: { endpoint: string; keys: { p256dh: string; auth: string } }) {
    return this.request<void>('POST', '/api/push/subscriptions', body);
  }

  pushUnsubscribe(endpoint: string) {
    return this.request<void>('DELETE', '/api/push/subscriptions', { endpoint });
  }

  pushTest() {
    return this.request<{ devices: number }>('POST', '/api/push/test');
  }

  /** Снимок страницы брони из встроенного браузера → черновик брони. */
  captureStayBooking(body: StayCaptureInput) {
    return this.request<StayCaptureResultDto>('POST', '/api/stays/bookings/capture', body);
  }

  deleteSection(id: string) {
    return this.request<void>('DELETE', `/api/sections/${id}`);
  }

  // ── Узлы Telegram Service (только администратор) ──────────────────────────

  telegramNodes() {
    return this.request<TelegramNodesDto>('GET', '/api/telegram-nodes');
  }

  /** Опрос узлов прямо сейчас. Может занять до нескольких секунд. */
  telegramNodesHealth() {
    return this.request<{ items: TelegramNodeHealthDto[] }>('GET', '/api/telegram-nodes/health');
  }

  createTelegramNode(body: { key: string; label: string; baseUrl: string; capacity: number }) {
    return this.request<unknown>('POST', '/api/telegram-nodes', body);
  }

  updateTelegramNode(
    id: string,
    body: { label?: string; baseUrl?: string; enabled?: boolean; capacity?: number },
  ) {
    return this.request<unknown>('PATCH', `/api/telegram-nodes/${id}`, body);
  }

  deleteTelegramNode(id: string) {
    return this.request<{ ok: true }>('DELETE', `/api/telegram-nodes/${id}`);
  }

  /** Переселить аккаунт. nodeId null — на ядро. Каталог TDLib не переносит. */
  moveTelegramAccount(telegramAccountId: string, nodeId: string | null) {
    return this.request<{ ok: true; warning: string }>(
      'POST',
      '/api/telegram-nodes/move-account',
      { telegramAccountId, nodeId },
    );
  }

  /** Выдать доступ к разделу. Ровно один адресат: сотрудник, роль или папка. */
  grantSection(id: string, subject: { userId?: string; role?: string; folderId?: string }) {
    return this.request<{ ok: true }>('POST', `/api/sections/${id}/access`, subject);
  }

  /** Отозвать выдачу по её идентификатору, а не по сотруднику: см. маршрут. */
  revokeSection(id: string, accessId: string) {
    return this.request<void>('DELETE', `/api/sections/${id}/access/${accessId}`);
  }

  // ── Папки Telegram-аккаунтов ──────────────────────────────────────────────

  accountFolders() {
    return this.request<AccountFolderTreeDto>('GET', '/api/account-folders');
  }

  createAccountFolder(body: { name: string; track: string; parentId?: string | null }) {
    return this.request<AccountFolderDto>('POST', '/api/account-folders', body);
  }

  renameAccountFolder(id: string, name: string) {
    return this.request<AccountFolderDto>('PATCH', `/api/account-folders/${id}`, { name });
  }

  deleteAccountFolder(id: string) {
    return this.request<void>('DELETE', `/api/account-folders/${id}`);
  }

  moveAccountToFolder(telegramAccountId: string, folderId: string | null) {
    return this.request<void>('POST', '/api/account-folders/move-account', {
      telegramAccountId,
      folderId,
    });
  }

  moveUserToFolder(userId: string, folderId: string | null) {
    return this.request<void>('POST', '/api/account-folders/move-user', { userId, folderId });
  }

  unreadCounts() {
    return this.request<{ counts: Record<string, number> }>('GET', '/api/inbox/unread');
  }

  chat(chatId: string) {
    return this.request<TelegramChatDto>('GET', `/api/chats/${chatId}`);
  }

  messages(chatId: string, params: { limit?: number; cursor?: string } = {}) {
    return this.request<{ items: TelegramMessageDto[]; nextCursor: string | null }>(
      'GET',
      `/api/chats/${chatId}/messages${buildQuery(params)}`,
    );
  }

  /**
   * Отправка.
   *
   * `telegramAccountId` обязателен: клиент подтверждает, от какого аккаунта, по
   * его представлению, уходит сообщение. Сервер сверяет и отвечает 409 при
   * расхождении (§28 ТЗ).
   */
  sendMessage(chatId: string, input: SendMessageInput) {
    return this.request<TelegramMessageDto>('POST', `/api/chats/${chatId}/messages`, input);
  }

  /** Закрепить или открепить чат в самом Telegram. */
  pinChat(chatId: string, pinned: boolean) {
    return this.request<TelegramChatDto>('POST', `/api/chats/${chatId}/pin`, { pinned });
  }

  /** Отметить чат прочитанным в Telegram: счётчик непрочитанного обнуляется. */
  markRead(chatId: string, telegramMessageIds: string[] = []) {
    return this.request<{ ok: true }>('POST', `/api/chats/${chatId}/read`, { telegramMessageIds });
  }

  editMessage(chatId: string, telegramMessageId: string, text: string) {
    return this.request<unknown>(
      'PATCH',
      `/api/chats/${chatId}/messages/${telegramMessageId}`,
      { text },
    );
  }

  deleteMessage(chatId: string, telegramMessageId: string) {
    return this.request<{ ok: true }>(
      'DELETE',
      `/api/chats/${chatId}/messages/${telegramMessageId}`,
    );
  }

  /**
   * Отправка файла.
   *
   * Идёт мимо `request`: тот кладёт тело как JSON, а здесь тело — сами байты.
   * Метаданные едут заголовками, а не в строке запроса, потому что подпись это
   * текст сообщения клиенту, а строка запроса попадает в журналы доступа.
   *
   * Значения заголовков кодируются: имя файла и подпись содержат кириллицу и
   * переводы строк, а заголовок обязан быть латиницей в одну строку.
   */
  async sendMedia(
    chatId: string,
    file: File,
    input: {
      telegramAccountId: string;
      kind: MediaKind;
      caption?: string;
      selfDestructSeconds?: number;
      /** Сторона квадрата видеосообщения. Только для kind = 'videoNote'. */
      videoNoteLength?: number;
      /**
       * Длительность в секундах. Для голосового обязательна: TDLib её сам не
       * измеряет, и без неё сообщение уходит как «0:00».
       */
      durationSeconds?: number;
    },
  ): Promise<TelegramMessageDto> {
    const response = await fetch(`/api/chats/${chatId}/media`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'content-type': file.type || 'application/octet-stream',
        'x-telegram-account-id': input.telegramAccountId,
        'x-media-kind': input.kind,
        ...csrfHeaders('POST'),
        ...(input.caption ? { 'x-caption': encodeURIComponent(input.caption) } : {}),
        ...(input.selfDestructSeconds
          ? { 'x-self-destruct': String(input.selfDestructSeconds) }
          : {}),
        ...(input.videoNoteLength
          ? { 'x-video-note-length': String(input.videoNoteLength) }
          : {}),
        ...(input.durationSeconds ? { 'x-duration': String(input.durationSeconds) } : {}),
      },
      body: file,
    });

    if (!response.ok) {
      const body: unknown = await response.json().catch(() => null);
      const error =
        body && typeof body === 'object' && 'error' in body
          ? (body as { error: { code?: string; message?: string } }).error
          : null;
      throw new ApiError(
        response.status,
        error?.code ?? 'MEDIA_SEND_FAILED',
        error?.message ?? 'Не удалось отправить файл',
      );
    }

    return (await response.json()) as TelegramMessageDto;
  }

  /**
   * Массовое действие над чатами.
   *
   * Возвращает число применённых и список пропущенных: недоступные чаты не
   * роняют операцию целиком, но и не исчезают молча — пользователь должен
   * понимать, что часть выбранного осталась нетронутой.
   */
  /**
   * Переслать сообщения. Возвращает сколько реально ушло: Telegram может
   * запретить пересылку из чата-источника, и часть пачки не пройдёт.
   */
  forwardMessages(chatId: string, targetChatId: string, messageIds: string[]) {
    return this.request<{ forwarded: number; requested: number }>(
      'POST',
      `/api/chats/${chatId}/forward`,
      { targetChatId, messageIds },
    );
  }

  chatMembers(chatId: string) {
    return this.request<ChatMembersDto>('GET', `/api/chats/${chatId}/members`);
  }

  /** Профиль собеседника — как в карточке Telegram. */
  chatProfile(chatId: string) {
    return this.request<ChatProfileDto>('GET', `/api/chats/${chatId}/profile`);
  }

  /** Активные истории собеседника. Пустой список — историй нет. */
  chatStories(chatId: string) {
    return this.request<{ items: StoryDto[] }>('GET', `/api/chats/${chatId}/stories`);
  }

  listFolders() {
    return this.request<{ items: FolderDto[] }>('GET', '/api/folders');
  }

  createFolder(input: { name: string; color?: string | null }) {
    return this.request<FolderDto>('POST', '/api/folders', input);
  }

  updateFolder(id: string, patch: { name?: string; color?: string | null }) {
    return this.request<FolderDto>('PATCH', `/api/folders/${id}`, patch);
  }

  deleteFolder(id: string) {
    return this.request<void>('DELETE', `/api/folders/${id}`);
  }

  setChatFolder(folderId: string, chatId: string, attached: boolean) {
    return this.request<void>('POST', `/api/folders/${folderId}/chats`, { chatId, attached });
  }

  bulkChats(input: {
    chatIds: string[];
    action: 'archive' | 'status' | 'delete';
    status?: string;
  }) {
    return this.request<{ applied: number; skipped: string[] }>('POST', '/api/chats/bulk', input);
  }

  updateChat(chatId: string, patch: ChatMetadataPatch) {
    return this.request<TelegramChatDto>('PATCH', `/api/chats/${chatId}`, patch);
  }

  assignChat(chatId: string, assignedManagerId: string | null) {
    return this.request<TelegramChatDto>('POST', `/api/chats/${chatId}/assign`, {
      assignedManagerId,
    });
  }

  // ── Администрирование ──────────────────────────────────────────────────────

  listUsers() {
    return this.request<{ items: Array<UserDto | UserSummaryDto> }>('GET', '/api/users');
  }

  /** Правка карточки: имя и логин. Пароль меняется отдельным вызовом. */
  updateUser(userId: string, patch: { displayName?: string; email?: string }) {
    return this.request<UserDto>('PATCH', `/api/users/${userId}`, patch);
  }

  /**
   * Задать сотруднику новый пароль.
   *
   * Именно задать, а не «посмотреть»: в базе лежит хеш, и текущий пароль
   * получить неоткуда. Все сессии сотрудника при этом обрываются.
   */
  setUserPassword(userId: string, password: string) {
    return this.request<UserDto>('POST', `/api/users/${userId}/password`, { password });
  }

  createUser(input: { email: string; displayName: string; role: string; password: string }) {
    return this.request<UserDto>('POST', '/api/users', input);
  }

  changeUserRole(userId: string, role: string) {
    return this.request<UserDto>('POST', `/api/users/${userId}/role`, { role });
  }

  setUserStatus(userId: string, status: string) {
    return this.request<UserDto>('POST', `/api/users/${userId}/status`, { status });
  }

  listAssignments(telegramAccountId: string) {
    return this.request<{ items: AssignmentDto[] }>(
      'GET',
      `/api/assignments?telegramAccountId=${telegramAccountId}`,
    );
  }

  /**
   * Удалить сотрудника насовсем.
   *
   * Возможно, только пока за ним ничего не числится. Иначе сервер отвечает
   * отказом с перечнем того, что мешает, — его и показываем администратору.
   */
  deleteUser(id: string) {
    return this.request<{ ok: true }>('DELETE', `/api/users/${id}`);
  }

  assign(input: { telegramAccountId: string; userId: string; accessLevel: string }) {
    return this.request<AssignmentDto>('POST', '/api/assignments', input);
  }

  revokeAssignment(assignmentId: string) {
    return this.request<AssignmentDto>('DELETE', `/api/assignments/${assignmentId}`);
  }

  assignSenior(telegramAccountId: string, seniorManagerId: string) {
    return this.request<unknown>('POST', '/api/assignments/senior', {
      telegramAccountId,
      seniorManagerId,
    });
  }

  audit(params: AuditParams = {}) {
    return this.request<{ items: AuditEventDto[]; nextCursor: string | null }>(
      'GET',
      `/api/audit${buildQuery(params)}`,
    );
  }
}

// ── Вспомогательные типы ─────────────────────────────────────────────────────

export interface StickerDto {
  fileId: string;
  emoji: string | null;
  setId: string;
  width: number;
  height: number;
  animated: boolean;
}

export interface StickerSetDto {
  id: string;
  title: string;
  stickers: StickerDto[];
}

/** Узел Telegram Service — отдельный сервер с TDLib-сессиями. */
export interface TelegramNodeDto {
  id: string;
  key: string;
  label: string;
  baseUrl: string;
  /** Выключенный узел не получает новых аккаунтов, старые работают. */
  enabled: boolean;
  /** Потолок аккаунтов. 0 — без ограничения. */
  capacity: number;
  accounts: number;
}

export interface TelegramNodesDto {
  /** Ядро: записи в базе нет, выключить и удалить нельзя. */
  core: { key: string; label: string; baseUrl: string; accounts: number };
  items: TelegramNodeDto[];
  /** Где живёт каждый аккаунт. nodeId null — ядро. */
  placement: Array<{ telegramAccountId: string; nodeId: string | null }>;
}

export interface TelegramNodeHealthDto {
  key: string;
  state: 'ok' | 'token' | 'unexpected' | 'down';
  latencyMs: number | null;
  driver: string | null;
}

/** Раздел рабочего места: CRM, сервис WhatsApp, свободный браузер. */
export interface SectionDto {
  id: string;
  key: string;
  title: string;
  kind: 'EMBEDDED' | 'BROWSER';
  /** Адрес. У BROWSER — стартовая страница, может отсутствовать. */
  url: string | null;
  /** Белый список хостов для BROWSER. Пустой — без ограничений. */
  allowedHosts: string[];
  enabled: boolean;
  sortOrder: number;
  /**
   * Селекторы формы входа рабочей системы.
   *
   * Пусто — подстановки нет, сотрудник входит руками. Заполняет администратор.
   */
  loginSelector: string | null;
  passwordSelector: string | null;
  submitSelector: string | null;
}

/** Моя пара для входа в рабочую систему. Пароль здесь НЕ передаётся. */
export interface SectionCredentialDto {
  sectionId: string;
  login: string;
  hasPassword: boolean;
  updatedAt: string;
}

export interface ManagedSectionDto extends SectionDto {
  access: SectionGrantDto[];
}

/**
 * Результат поиска по Telegram.
 *
 * `chatId` — наш UUID, если чат уже заведён, иначе null. Различие определяет
 * действие: известный чат открывается, неизвестный сначала заводится. Решает
 * это сервер, у которого есть база; клиенту угадывать нечем.
 */
export interface TelegramSearchItemDto {
  telegramChatId: string;
  title: string | null;
  type: string;
  username: string | null;
  telegramUserId: string | null;
  chatId: string | null;
}

export type CodeChannel =
  | 'telegramMessage'
  | 'sms'
  | 'call'
  | 'flashCall'
  | 'missedCall'
  | 'fragment'
  | 'email'
  | 'other';

export interface AuthStateDto {
  stage: 'WAIT_PHONE' | 'WAIT_CODE' | 'WAIT_PASSWORD' | 'WAIT_QR_SCAN' | 'READY' | 'CLOSED';
  qrLoginUrl?: string;
  passwordHint?: string;
  /** Куда ушёл код. Старый сервис поля не присылает — читать защищённо. */
  codeChannel?: CodeChannel;
  nextCodeChannel?: CodeChannel;
  codeTimeoutSeconds?: number;
  codeLength?: number;
  emailPattern?: string;
}

export interface ChatProfileDto {
  kind: 'user' | 'bot' | 'group' | 'channel';
  title: string | null;
  username?: string | null;
  phone?: string | null;
  bio?: string | null;
  description?: string | null;
  memberCount?: number | null;
  lastSeen?: {
    kind: 'online' | 'offline' | 'recently' | 'lastWeek' | 'lastMonth' | 'hidden';
    at?: string;
  };
  isPremium?: boolean;
  isVerified?: boolean;
  isScam?: boolean;
  hasStories: boolean;
}

export interface StoryDto {
  id: number;
  kind: 'photo' | 'video';
  date: string;
  expiresAt: string | null;
  caption: string | null;
  entities: TelegramTextEntityDto[];
  duration: number | null;
  viewed: boolean;
}

export interface ChatMembersDto {
  kind: string;
  memberCount: number;
  /** Ложь означает «Telegram не раскрывает состав», а не «участников нет». */
  canViewMembers: boolean;
  members: Array<{
    telegramUserId: string | null;
    status: string;
    displayName?: string | null;
    username?: string | null;
  }>;
}

export interface FolderDto {
  id: string;
  name: string;
  color: string | null;
  sortOrder?: number;
  chatCount?: number;
}

export type MediaKind =
  | 'photo'
  | 'video'
  | 'animation'
  | 'audio'
  | 'voice'
  | 'videoNote'
  | 'document';

export interface QuickReplyAttachmentDto {
  id: string;
  kind: 'photo' | 'video';
  mimeType: string;
  fileName: string | null;
  size: number;
  sortOrder: number;
}

export interface QuickReplyDto {
  id: string;
  title: string;
  text: string;
  /**
   * Разметка текста: премиальные эмодзи, жирный, ссылки. Появляется у
   * заготовок, сохранённых из сообщения. Старый API поля не присылает.
   */
  entities?: TelegramTextEntityDto[];
  /** Фото и видео, уходящие вместе с текстом. Старый API поля не присылает. */
  attachments?: QuickReplyAttachmentDto[];
  sortOrder: number;
  /** Папка. null — общая заготовка, видная всем, кто видит быстрые ответы. */
  folderId?: string | null;
  /** Имя папки. Приходит в списке, чтобы не тянуть справочник ради заголовка. */
  folderName?: string | null;
  /** Кто завёл заготовку. Приходит только в списке, при создании — нет. */
  author?: string;
  updatedAt?: string;
}

/** Папка быстрых ответов вместе с выдачами доступа. */
export interface QuickReplyFolderDto {
  id: string;
  name: string;
  sortOrder: number;
  access: Array<{
    id: string;
    userId: string | null;
    role: string | null;
    teamId: string | null;
    user: { id: string; displayName: string; role: string } | null;
    team: { id: string; name: string; track: string; parentId: string | null } | null;
  }>;
  _count: { replies: number };
}

export interface AssignmentDto {
  id: string;
  telegramAccountId: string;
  user: UserSummaryDto;
  accessLevel: string;
  effectiveAccessLevel: string;
  createdAt: string;
}

export interface CustomEmojiSetDto {
  id: string;
  title: string;
  items: Array<{ customEmojiId: string; emoji: string }>;
}

export interface TgFolderDto {
  tgId: number;
  title: string;
  emoji: string | null;
  position: number;
}

export interface InboxParams {
  /** Показать архив вместо рабочего списка. По умолчанию архив скрыт. */
  archived?: boolean;
  folderId?: string;
  limit?: number;
  cursor?: string;
  telegramAccountId?: string;
  /** Набор аккаунтов — при выборе команды целиком. */
  telegramAccountIds?: string[];
  /** Папка Telegram выбранного аккаунта (tgId). */
  tgFolderId?: number;
  unread?: boolean;
  status?: string;
  managerId?: string;
  search?: string;
  tags?: string[];
}

export interface AuditParams {
  limit?: number;
  cursor?: string;
  action?: string;
  telegramAccountId?: string;
  outcome?: string;
}

export interface SendMessageInput {
  telegramAccountId: string;
  text: string;
  replyToTelegramMessageId?: string;
  idempotencyKey: string;
}

export interface ChatMetadataPatch {
  internalStatus?: string;
  tags?: string[];
  internalNotes?: string | null;
}

function buildQuery(params: object): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === false) continue;
    search.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }

  const query = search.toString();
  return query ? `?${query}` : '';
}

function describeDevice(): string {
  const desktop = window.__tw_desktop;
  return desktop ? `BUCOR Workspace Desktop ${desktop.getVersion()}` : 'Браузер';
}

/**
 * Ключ идемпотентности генерируется КЛИЕНТОМ до отправки.
 *
 * Смысл именно в этом: при обрыве связи клиент повторяет запрос с тем же
 * ключом, и сервер понимает, что это ретрай, а не второе сообщение. Ключ,
 * созданный сервером, такой защиты не даёт.
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export const api = new ApiClient();

/**
 * Одна выдача доступа к разделу.
 *
 * Заполнено ровно одно из трёх полей адресата — так же, как в базе. Читать это
 * нужно через `subject`, а не проверками на null подряд: три параллельных
 * `if` расходятся при первом же изменении, а неучтённая ветка выглядит как
 * пустая строка в списке, а не как ошибка.
 */
export interface SectionGrantDto {
  id: string;
  createdAt: string;
  userId: string | null;
  role: string | null;
  folderId: string | null;
  user: { id: string; displayName: string; role: string; status: string } | null;
  folder: { id: string; name: string; track: string; parentId: string | null } | null;
}

/** Папка Telegram-аккаунтов. */
export interface AccountFolderDto {
  id: string;
  name: string;
  track: 'MANAGER' | 'OPERATOR' | 'HR';
  parentId: string | null;
}

export interface AccountFolderTreeDto {
  folders: AccountFolderDto[];
  accounts: Array<{ id: string; alias: string; folderId: string | null }>;
  users: Array<{ id: string; displayName: string; role: string; folderId: string | null }>;
}

export interface StayBookingQuery {
  from?: string;
  to?: string;
  status?: 'CONFIRMED' | 'CANCELLED';
  /** Только черновики (true) или только заполненные (false). */
  draft?: 'true' | 'false';
  accountId?: string;
  managerId?: string;
  propertyId?: string;
  platform?: StayPlatform;
  q?: string;
  /** Кто живёт — по части имени. */
  worker?: string;
  city?: string;
  country?: string;
  propertyStatus?: StayPropertyStatus;
  limit?: number;
  cursor?: string;
}

export interface StayBookingInput {
  accountId?: string | null;
  propertyId?: string | null;
  title?: string;
  url?: string | null;
  bookingUrl?: string | null;
  city?: string | null;
  country?: string | null;
  checkIn: string;
  checkOut: string;
  /** В обычных единицах (123.45); сервер хранит в минимальных. */
  price?: number | null;
  currency?: string | null;
  managerId?: string | null;
  workerName: string;
  status?: 'CONFIRMED' | 'CANCELLED';
  confirmationCode?: string | null;
  chatUrl?: string | null;
  note?: string | null;
}
